import { useDebounceCallback } from '@react-hook/debounce'
import { useDispatch, useSelector } from 'react-redux'
import { doSetEstimateOrderRequestData, doSetEstimateOrderRequestDataSnapshot } from '@/stores/reduxData'
import { RootState } from '@/stores/store'

const DebounceTime = 400

/**
 * a와 b가 동일한지 확인
 * a 또는 b가 undefined 또는 null 인 경우도 동일하다고 간주하고 true 반환
 */
function compareWithNullUndefined(a: unknown, b: unknown): boolean {
  // a와 b가 모두 undefined 또는 null 인 경우 true 반환
  if ((a === undefined || a === null) && (b === undefined || b === null)) {
    return true
  }

  return a === b
}

export const useSetEstimateOrder = () => {
  const dispatch = useDispatch()

  const estimatedOrderRequest = useSelector((rootState: RootState) => rootState.reduxDataReducers.estimatedOrderRequest)

  const setEstimateOrderByMileage = useDebounceCallback((mileage: number | undefined) => {
    if (!estimatedOrderRequest) {
      return
    }
    const payload = {
      ...estimatedOrderRequest,
      mileage,
    }
    dispatch(doSetEstimateOrderRequestData(payload))
  }, DebounceTime)

  const setEstimateOrderByOrderCoupon = (orderCouponId: number | undefined) => {
    if (!estimatedOrderRequest) {
      return
    }
    const payload = {
      ...estimatedOrderRequest,
      orderCouponId,
    }
    dispatch(doSetEstimateOrderRequestData(payload))
  }

  const setEstimateOrderByCustomerTemplateAddress = (customerTemplateAddressId: number | undefined) => {
    if (!estimatedOrderRequest) {
      return
    }
    // 동일한 배송지면 불필요한 재견적 요청을 막는다
    if (compareWithNullUndefined(estimatedOrderRequest.customerTemplateAddressId, customerTemplateAddressId)) {
      return
    }
    const payload = {
      ...estimatedOrderRequest,
      customerTemplateAddressId,
    }
    dispatch(doSetEstimateOrderRequestData(payload))
  }

  const setEstimateOrderByProductCoupon = ({
    productOptionId,
    productPromotionId,
    productCouponId,
  }: {
    productOptionId: number
    productPromotionId: number | undefined
    productCouponId: number | undefined
  }) => {
    if (!estimatedOrderRequest) {
      return
    }

    // 상품 쿠폰 데이터를 request 데이터에 반영하기 전에 snapshot 데이터를 저장한다.
    dispatch(doSetEstimateOrderRequestDataSnapshot(estimatedOrderRequest))

    const shippings = estimatedOrderRequest.shippings.map((shipping) => {
      const products = shipping.products.map((orderItem) => {
        if (
          orderItem.productOptionId === productOptionId &&
          compareWithNullUndefined(orderItem.productPromotionId, productPromotionId)
        ) {
          return {
            ...orderItem,
            productCouponId,
          }
        }
        return orderItem
      })
      return { products }
    })
    const payload = {
      ...estimatedOrderRequest,
      shippings,
    }
    dispatch(doSetEstimateOrderRequestData(payload))
  }

  const setEstimateOrderByShipmentCoupon = ({
    shippingIndex,
    shippingCouponId,
  }: {
    shippingIndex: number
    shippingCouponId: number | undefined
  }) => {
    if (!estimatedOrderRequest) {
      return
    }
    const shippings = estimatedOrderRequest.shippings.map((shipping, index) => {
      if (index === shippingIndex) {
        return {
          ...shipping,
          shippingCouponId,
        }
      }
      return shipping
    })
    const payload = {
      ...estimatedOrderRequest,
      shippings,
    }
    dispatch(doSetEstimateOrderRequestData(payload))
  }

  return {
    setEstimateOrderByMileage,
    setEstimateOrderByOrderCoupon,
    setEstimateOrderByCustomerTemplateAddress,
    setEstimateOrderByProductCoupon,
    setEstimateOrderByShipmentCoupon,
  }
}
